import React, { useState, useRef } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter,
  ModalCloseButton, Button, VStack, HStack, Text, Box, useToast, Flex,
  Alert, AlertIcon, AlertDescription, Divider, Progress, Icon,
} from '@chakra-ui/react';
import { FiDownload, FiUpload, FiShield, FiAlertTriangle } from 'react-icons/fi';
import api from '../api';

export default function BackupRestoreModal({ isOpen, onClose }) {
  const [exporting, setExporting] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreFile, setRestoreFile] = useState(null);
  const [confirmRestore, setConfirmRestore] = useState(false);
  const fileInputRef = useRef(null);
  const toast = useToast();

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get('/backup/export', { responseType: 'blob' });
      // Extract filename from header or generate one
      const disposition = response.headers['content-disposition'];
      let filename = 'forgebos_backup.sql';
      if (disposition) {
        const match = disposition.match(/filename="?([^"]+)"?/);
        if (match) filename = match[1];
      }
      // Download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast({ title: 'Backup downloaded', status: 'success', duration: 2000 });
    } catch (err) {
      toast({ title: 'Backup failed', description: err.response?.data?.error || 'Server error', status: 'error', duration: 3000 });
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.endsWith('.sql')) {
      toast({ title: 'Please select a .sql file', status: 'warning', duration: 2000 });
      return;
    }
    setRestoreFile(file);
    setConfirmRestore(false);
  };

  const handleRestore = async () => {
    if (!restoreFile) return;
    if (!confirmRestore) {
      setConfirmRestore(true);
      return;
    }

    setRestoring(true);
    try {
      // Step 1: Auto-download a backup first (client-side safety net)
      toast({ title: 'Creating pre-restore backup...', status: 'info', duration: 2000 });
      try {
        const preBackup = await api.get('/backup/export', { responseType: 'blob' });
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
        const url = window.URL.createObjectURL(new Blob([preBackup.data]));
        const a = document.createElement('a');
        a.href = url;
        a.download = `pre_restore_backup_${timestamp}.sql`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (preErr) {
        toast({ title: 'Pre-restore backup failed — restore aborted', status: 'error', duration: 4000 });
        setRestoring(false);
        return;
      }

      // Step 2: Read file and send to server
      toast({ title: 'Restoring database...', status: 'info', duration: 3000 });
      const sql = await restoreFile.text();
      const { data } = await api.post('/backup/restore', { sql }, {
        timeout: 300000, // 5 min timeout
      });

      toast({ title: 'Restore complete', description: 'A pre-restore backup was also saved on the server.', status: 'success', duration: 4000 });
      setRestoreFile(null);
      setConfirmRestore(false);
    } catch (err) {
      toast({
        title: 'Restore failed',
        description: err.response?.data?.error || 'Server error',
        status: 'error',
        duration: 5000,
      });
    } finally {
      setRestoring(false);
    }
  };

  const handleClose = () => {
    setRestoreFile(null);
    setConfirmRestore(false);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader fontSize="md">
          <Flex align="center" gap={2}>
            <Icon as={FiShield} />
            Data Backup / Restore
          </Flex>
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack spacing={5} align="stretch">

            {/* Export Section */}
            <Box>
              <Text fontSize="sm" fontWeight="600" mb={2}>Download Your Data</Text>
              <Text fontSize="xs" color="gray.500" mb={3}>
                Exports <strong>your account data only</strong> as a .sql file — companies, contacts, projects, invoices, communications, HR records, and settings. Other tenants' data is never included. Use this for your own records or migration purposes.
              </Text>
              <Button
                leftIcon={<FiDownload />}
                colorScheme="blue"
                size="sm"
                onClick={handleExport}
                isLoading={exporting}
                loadingText="Exporting..."
                w="100%"
              >
                Download Database Backup
              </Button>
            </Box>

            <Divider />

            {/* Restore Section */}
            <Box>
              <Text fontSize="sm" fontWeight="600" mb={2}>Restore from Backup</Text>
              <Alert status="warning" borderRadius="md" mb={3} fontSize="xs">
                <AlertIcon boxSize={4} />
                <AlertDescription>
                  This will replace ALL data in the database. A backup of the current data will be downloaded automatically before the restore begins.
                </AlertDescription>
              </Alert>

              <input
                type="file"
                accept=".sql"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />

              {!restoreFile ? (
                <Button
                  leftIcon={<FiUpload />}
                  colorScheme="orange"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  w="100%"
                >
                  Select .sql File to Restore
                </Button>
              ) : (
                <VStack spacing={2} align="stretch">
                  <Box p={2} bg="orange.50" borderRadius="md" border="1px" borderColor="orange.200">
                    <Text fontSize="12px" fontWeight="500">{restoreFile.name}</Text>
                    <Text fontSize="10px" color="gray.500">
                      {(restoreFile.size / 1024 / 1024).toFixed(2)} MB
                    </Text>
                  </Box>

                  {confirmRestore && (
                    <Alert status="error" borderRadius="md" fontSize="xs">
                      <AlertIcon boxSize={4} />
                      <AlertDescription>
                        Are you sure? This will overwrite all current data. A pre-restore backup will be saved automatically.
                      </AlertDescription>
                    </Alert>
                  )}

                  {restoring && <Progress size="xs" isIndeterminate colorScheme="orange" />}

                  <HStack spacing={2}>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => { setRestoreFile(null); setConfirmRestore(false); }}
                      isDisabled={restoring}
                    >
                      Cancel
                    </Button>
                    <Button
                      leftIcon={confirmRestore ? <FiAlertTriangle /> : <FiUpload />}
                      colorScheme={confirmRestore ? 'red' : 'orange'}
                      size="sm"
                      onClick={handleRestore}
                      isLoading={restoring}
                      loadingText="Restoring..."
                      flex={1}
                    >
                      {confirmRestore ? 'Yes, Restore Now' : 'Restore Database'}
                    </Button>
                  </HStack>
                </VStack>
              )}
            </Box>

          </VStack>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" onClick={handleClose}>Close</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
